import { useEffect, useRef } from "react";
import {
  NODE_LINK_DISTANCE, POINTER_LINKS, POINTER_RADIUS,
  createNodes, nearestNodes, stepNodes, type GraphNode,
} from "./webGraph";

const REDUCED = "(prefers-reduced-motion: reduce)";

/** A drifting field of nodes. Hovering pulls a web of lines from the pointer to the nearest
    nodes, like a spider web. Decorative only; with reduced motion it draws a single still frame. */
export function WebCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    const host = canvas.parentElement ?? canvas;
    const still = typeof window.matchMedia === "function" && window.matchMedia(REDUCED).matches;
    const ink = getComputedStyle(canvas).getPropertyValue("--accent").trim() || "#1F4585";

    let width = 0;
    let height = 0;
    let nodes: GraphNode[] = [];
    const pointer = { x: 0, y: 0, strength: 0, target: 0 };
    let frame = 0;

    function resize() {
      const rect = host.getBoundingClientRect();
      const ratio = window.devicePixelRatio || 1;
      width = Math.max(rect.width, 1);
      height = Math.max(rect.height, 1);
      canvas!.width = width * ratio;
      canvas!.height = height * ratio;
      ctx!.setTransform(ratio, 0, 0, ratio, 0, 0);
      nodes = createNodes(Math.min(90, Math.max(30, Math.round((width * height) / 14000))), width, height);
    }

    function line(x1: number, y1: number, x2: number, y2: number, alpha: number, w: number) {
      ctx!.globalAlpha = alpha;
      ctx!.lineWidth = w;
      ctx!.beginPath();
      ctx!.moveTo(x1, y1);
      ctx!.lineTo(x2, y2);
      ctx!.stroke();
    }

    function draw() {
      ctx!.clearRect(0, 0, width, height);
      ctx!.strokeStyle = ink;
      ctx!.fillStyle = ink;
      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i]!;
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j]!;
          const d = Math.hypot(a.x - b.x, a.y - b.y);
          if (d < NODE_LINK_DISTANCE) line(a.x, a.y, b.x, b.y, (1 - d / NODE_LINK_DISTANCE) * 0.22, 1);
        }
      }
      if (pointer.strength > 0.01) {
        for (const { node, dist } of nearestNodes(nodes, pointer.x, pointer.y, POINTER_LINKS, POINTER_RADIUS)) {
          line(pointer.x, pointer.y, node.x, node.y, (1 - dist / POINTER_RADIUS) * 0.75 * pointer.strength, 1.4);
        }
        ctx!.globalAlpha = 0.9 * pointer.strength;
        ctx!.beginPath();
        ctx!.arc(pointer.x, pointer.y, 3.5, 0, Math.PI * 2);
        ctx!.fill();
      }
      ctx!.globalAlpha = 0.55;
      for (const n of nodes) {
        ctx!.beginPath();
        ctx!.arc(n.x, n.y, 2, 0, Math.PI * 2);
        ctx!.fill();
      }
    }

    function tick() {
      stepNodes(nodes, width, height);
      pointer.strength += (pointer.target - pointer.strength) * 0.12;
      if (pointer.strength > 0.05) {
        for (const { node, dist } of nearestNodes(nodes, pointer.x, pointer.y, POINTER_LINKS, POINTER_RADIUS)) {
          const pull = (1 - dist / POINTER_RADIUS) * 0.012 * pointer.strength;
          node.x += (pointer.x - node.x) * pull;
          node.y += (pointer.y - node.y) * pull;
        }
      }
      draw();
      frame = requestAnimationFrame(tick);
    }

    function onMove(e: PointerEvent) {
      const rect = host.getBoundingClientRect();
      pointer.x = e.clientX - rect.left;
      pointer.y = e.clientY - rect.top;
      pointer.target = 1;
    }
    const onLeave = () => { pointer.target = 0; };

    resize();
    draw();
    const observer = new ResizeObserver(() => { resize(); draw(); });
    observer.observe(host);
    if (!still) {
      host.addEventListener("pointermove", onMove);
      host.addEventListener("pointerleave", onLeave);
      frame = requestAnimationFrame(tick);
    }
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      host.removeEventListener("pointermove", onMove);
      host.removeEventListener("pointerleave", onLeave);
    };
  }, []);

  return <canvas ref={canvasRef} className="signin-web" aria-hidden="true" />;
}
